// Copyright (c) Docugami, Inc. All rights reserved.

import { Shimmer } from "@fluentui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query/react";
import { modalShadow, primaryTheme } from "components/shell/presentation/AppStyles";
import { ReactNode, useRef } from "react";
import { folderIdSelector } from "store/dgml/dgmlSelectors";
import { useGetPageImageQuery } from "store/frontend/frontendSlice";
import styled from "styled-components";

const PagesContainer = styled.div`
    display: flex;
    flex-direction: column;
    padding: 20px;
    box-sizing: border-box;
    justify-content: center;
    align-items: center;
`;

const LoadingPage = styled.div`
    display: flex;
    flex-direction: column;
    padding: 10px;
    box-sizing: border-box;
    justify-content: space-around;
    height: 100%;
    width: 70%;
    background-color: ${primaryTheme.palette.white};
    overflow: hidden;
    box-shadow: ${modalShadow};
`;

const Image = styled.img`
    height: 100%;
    box-shadow: ${modalShadow};
`;

interface DocumentPreviewProps {
    documentId: string;
    clusterId: string;
}

export const DocumentPreviewPageRenderer = (props: DocumentPreviewProps): JSX.Element => {
    const folderId = folderIdSelector(props.documentId);
    const pageRef = useRef<HTMLDivElement>(null);

    const { currentData: pageImageUrl } = useGetPageImageQuery(!!folderId ? { fileId: folderId.fileId, page: 1 } : skipToken);

    return (
        <PagesContainer>
            {!!pageImageUrl ? <Image src={pageImageUrl} /> : <LoadingPage ref={pageRef}>{Array(20).fill(<Shimmer />)}</LoadingPage>}
        </PagesContainer>
    )
}


// This preview page placeholder has the same dimensions as the DocumentPreviewPageRenderer component,
// and can be used together with it to show placeholder content if needed
interface PreviewPagePlaceholderProps {
    children?: ReactNode;
}

export const PreviewPagePlaceholder = (props: PreviewPagePlaceholderProps): JSX.Element => {
    return (
        <PagesContainer>
            <LoadingPage>{props.children}</LoadingPage>
        </PagesContainer>
    )
}
